// ─── DDQuestionnaireTemplate.js ───────────────────────────────────────────────
// Comprehensive Third Party Due Diligence Questionnaire
// Covers FATF, FinCEN/BSA, FCA, EU AMLD5, MAS, and emerging market requirements
// ───────────────────────────────────────────────────────────────────────────────

export const DD_QUESTIONNAIRE_SECTIONS = [
  {
    id: "entity_identification",
    title: "Entity Identification & Ownership",
    icon: "🏢",
    description: "Verify entity existence, structure, and beneficial ownership",
    questions: [
      {
        id: "legal_name",
        question: "Legal name of the entity (as registered with authorities)",
        type: "text",
        required: true,
        regulatory: ["FATF", "FinCEN", "FCA", "AMLD5"],
      },
      {
        id: "registration_number",
        question: "Company/Business registration number or tax ID",
        type: "text",
        required: true,
        regulatory: ["FATF", "FinCEN", "FCA"],
      },
      {
        id: "registration_authority",
        question: "Jurisdiction and registration authority",
        type: "select",
        options: [
          "US (Secretary of State)", "UK (Companies House)", "EU (Local registry)",
          "Singapore (ACRA)", "Hong Kong (Companies Registry)", "Cayman Islands",
          "Other (please specify)"
        ],
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "incorporation_date",
        question: "Date of incorporation/establishment",
        type: "date",
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "business_type",
        question: "Type of business entity",
        type: "select",
        options: [
          "Corporation", "Partnership", "Sole Proprietorship", "Trust",
          "Foundation", "Cooperative", "Other"
        ],
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "ubo_structure",
        question: "Ultimate Beneficial Owner (UBO) structure. List all individuals with direct or indirect ownership >25%",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "AMLD5", "FCA"],
        hint: "Include: Name, nationality, ownership %, role, address"
      },
      {
        id: "controlling_persons",
        question: "Senior Managing Officials / Persons with control",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
        hint: "CEO, CFO, board members, or those exercising ultimate control"
      },
      {
        id: "org_structure",
        question: "Organizational structure. Provide org chart or description of subsidiaries/parent companies",
        type: "textarea",
        required: false,
        regulatory: ["FATF"],
      },
    ]
  },

  {
    id: "business_operations",
    title: "Business Operations & Activities",
    icon: "💼",
    description: "Understand the entity's business model, products, and customer base",
    questions: [
      {
        id: "business_description",
        question: "Detailed description of business activities and revenue sources",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "primary_services",
        question: "Primary services/products offered",
        type: "select",
        options: [
          "Payment Processing", "Banking", "Remittance", "VASP/Crypto",
          "Money Transfer", "Investment Management", "Trading",
          "Trade Finance", "Supply Chain", "Technology/SaaS", "Consulting",
          "Other (specify)"
        ],
        required: true,
        regulatory: ["FATF"],
        multiple: true
      },
      {
        id: "revenue_range",
        question: "Annual revenue range (last fiscal year)",
        type: "select",
        options: [
          "<$1M", "$1M-$10M", "$10M-$50M", "$50M-$100M",
          "$100M-$500M", "$500M-$1B", ">$1B"
        ],
        required: true,
        regulatory: ["FinCEN"],
      },
      {
        id: "transaction_volume",
        question: "Estimated annual transaction volume / number of customers",
        type: "text",
        required: false,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "customer_types",
        question: "Primary customer types and their geographic distribution",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
        hint: "E.g., 'Retail consumers (80%, US/EU), SME businesses (20%, LATAM)'"
      },
      {
        id: "high_risk_jurisdictions",
        question: "Does the entity conduct business in high-risk jurisdictions or OFAC-sanctioned countries?",
        type: "select",
        options: ["No", "Yes - please specify"],
        required: true,
        regulatory: ["FATF", "FinCEN", "OFAC"],
      },
      {
        id: "high_risk_jurisdictions_detail",
        question: "High-risk jurisdictions (if applicable)",
        type: "textarea",
        required: false,
        regulatory: ["FATF"],
        conditional: "high_risk_jurisdictions",
        conditionalValue: "Yes - please specify"
      },
      {
        id: "business_model_changes",
        question: "Has the business model or focus changed significantly in the last 3 years?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF"],
      },
    ]
  },

  {
    id: "regulatory_compliance",
    title: "Regulatory Status & Compliance Framework",
    icon: "⚖️",
    description: "Verify licenses, regulatory oversight, and AML/CFT procedures",
    questions: [
      {
        id: "regulated_entity",
        question: "Is the entity regulated by a financial regulator?",
        type: "select",
        options: ["No", "Yes - Primary Regulator", "Yes - Multiple Regulators"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "primary_regulator",
        question: "Primary regulator(s) and jurisdiction",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
        conditional: "regulated_entity",
        conditionalValue: ["Yes - Primary Regulator", "Yes - Multiple Regulators"]
      },
      {
        id: "regulatory_licenses",
        question: "List all applicable licenses/registrations held (e.g., MSB, VASP, banking license)",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
        hint: "Include license type, issuing authority, license number, renewal date"
      },
      {
        id: "aml_framework",
        question: "Does the entity have a documented AML/CFT compliance program?",
        type: "select",
        options: ["No", "Yes - Internal Program", "Yes - Third-Party Managed"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "aml_framework_details",
        question: "AML/CFT program details (policies, procedures, staff, training)",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
        conditional: "aml_framework",
        conditionalValue: ["Yes - Internal Program", "Yes - Third-Party Managed"]
      },
      {
        id: "kyc_procedures",
        question: "KYC/CIP procedures for customer onboarding",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
        hint: "How do you verify customer identity? What documents required?"
      },
      {
        id: "sanctions_screening",
        question: "Does the entity perform sanctions screening (OFAC, UN, EU, etc.)?",
        type: "select",
        options: ["No", "Yes - In-house", "Yes - Third-party vendor", "Yes - Both"],
        required: true,
        regulatory: ["FATF", "FinCEN", "OFAC"],
      },
      {
        id: "transaction_monitoring",
        question: "Transaction monitoring procedures and suspicious activity reporting (SAR) process",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
        hint: "Thresholds, rules, reporting timeline to authorities, MLRO escalation"
      },
      {
        id: "regulatory_violations",
        question: "Has the entity received any regulatory warnings, citations, fines, or enforcement actions in the last 5 years?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "regulatory_violations_detail",
        question: "Regulatory violations/enforcement actions (details)",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
        conditional: "regulatory_violations",
        conditionalValue: "Yes - please describe"
      },
    ]
  },

  {
    id: "beneficial_owners_pep",
    title: "Beneficial Owners & PEP Status",
    icon: "👥",
    description: "Screen beneficial owners for PEP/sanctioned status",
    questions: [
      {
        id: "pep_screening",
        question: "Have all UBOs and senior management been screened against PEP databases (OFAC, UN, EU, etc.)?",
        type: "select",
        options: ["No", "Yes - Screening performed", "Yes - No PEPs found"],
        required: true,
        regulatory: ["FATF", "FinCEN", "AMLD5"],
      },
      {
        id: "pep_screening_results",
        question: "PEP screening results and any matches found",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "adverse_media",
        question: "Has adverse media screening been conducted on UBOs/owners? Any negative news found?",
        type: "select",
        options: ["No screening done", "Yes - Clean", "Yes - Adverse media found"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "adverse_media_details",
        question: "Adverse media findings (if applicable)",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
        conditional: "adverse_media",
        conditionalValue: "Yes - Adverse media found"
      },
      {
        id: "politically_exposed",
        question: "Are any owners/senior management politically exposed persons (PEPs) or family members of PEPs?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF", "AMLD5", "FCA"],
      },
      {
        id: "politically_exposed_detail",
        question: "PEP details (position, country, family relationship)",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "AMLD5"],
        conditional: "politically_exposed",
        conditionalValue: "Yes - please describe"
      },
    ]
  },

  {
    id: "financial_integrity",
    title: "Financial Integrity & Banking Relationships",
    icon: "🏦",
    description: "Verify financial stability and banking relationships",
    questions: [
      {
        id: "banking_relationships",
        question: "List all primary banking relationships (current and past 2 years)",
        type: "textarea",
        required: true,
        regulatory: ["FATF", "FinCEN"],
        hint: "Bank name, jurisdiction, account type, purpose"
      },
      {
        id: "bank_correspondence",
        question: "Correspondent banking relationships (if applicable)",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "financial_statements",
        question: "Most recent audited financial statements available?",
        type: "select",
        options: ["No", "Yes - Last year", "Yes - Last 2 years", "Yes - Last 3 years"],
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "credit_rating",
        question: "Credit rating or credit bureau report (if available)",
        type: "text",
        required: false,
        regulatory: ["FATF"],
      },
      {
        id: "debt_obligations",
        question: "Outstanding debt or significant financial obligations?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "financial_litigation",
        question: "Any ongoing financial litigation, bankruptcy, or insolvency proceedings?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF"],
      },
    ]
  },

  {
    id: "third_party_relationships",
    title: "Third-Party Due Diligence",
    icon: "🤝",
    description: "Understand the entity's own third-party vendor management",
    questions: [
      {
        id: "third_party_vendors",
        question: "Does the entity work with third-party vendors, service providers, or agents?",
        type: "select",
        options: ["No", "Yes - Limited", "Yes - Multiple"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "third_party_vendor_list",
        question: "List of critical third-party vendors/service providers",
        type: "textarea",
        required: false,
        regulatory: ["FATF"],
        conditional: "third_party_vendors",
        conditionalValue: ["Yes - Limited", "Yes - Multiple"],
        hint: "Name, jurisdiction, relationship type, duration"
      },
      {
        id: "vendor_due_diligence",
        question: "Do you perform due diligence on third-party vendors?",
        type: "select",
        options: ["No", "Yes - Basic screening", "Yes - Comprehensive DD"],
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "outsourcing_risk",
        question: "Have you identified any high-risk outsourcing (AML, sanctions, data processing)?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
    ]
  },

  {
    id: "incident_history",
    title: "Incident & Regulatory History",
    icon: "⚠️",
    description: "Review past incidents, complaints, and enforcement actions",
    questions: [
      {
        id: "sar_filings",
        question: "Has the entity filed SARs (Suspicious Activity Reports) or similar regulatory disclosures?",
        type: "select",
        options: ["No", "Yes - 1-2", "Yes - 3-5", "Yes - More than 5"],
        required: true,
        regulatory: ["FinCEN"],
      },
      {
        id: "regulatory_complaints",
        question: "Customer complaints to regulators or ombudsman in the last 2 years?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF", "FCA"],
      },
      {
        id: "civil_litigation",
        question: "Ongoing or recent civil litigation (last 5 years)?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "criminal_history",
        question: "Any criminal investigations or convictions involving entity or owners?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "media_coverage",
        question: "Significant negative media coverage or public controversies?",
        type: "select",
        options: ["No", "Yes - please describe"],
        required: true,
        regulatory: ["FATF"],
      },
    ]
  },

  {
    id: "enhanced_due_diligence",
    title: "Enhanced Due Diligence (EDD) Triggers",
    icon: "🔍",
    description: "Identify if the entity requires additional scrutiny",
    questions: [
      {
        id: "edd_required",
        question: "Based on risk factors, Enhanced Due Diligence (EDD) is required?",
        type: "select",
        options: ["No", "Yes - Moderate EDD", "Yes - Strict EDD"],
        required: true,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "edd_triggers",
        question: "EDD triggers identified (select all that apply)",
        type: "checkbox",
        options: [
          "High-risk jurisdiction operations",
          "PEP involvement",
          "High transaction volume/unusual patterns",
          "Sanctioned country activity",
          "Cryptocurrency/VASP operations",
          "Cash-intensive business",
          "High-risk customer profile",
          "Complex ownership structure",
          "Regulatory enforcement history",
          "Adverse media findings"
        ],
        required: false,
        regulatory: ["FATF", "FinCEN"],
      },
      {
        id: "edd_measures",
        question: "Describe specific EDD measures implemented",
        type: "textarea",
        required: false,
        regulatory: ["FATF", "FinCEN"],
        conditional: "edd_required",
        conditionalValue: ["Yes - Moderate EDD", "Yes - Strict EDD"]
      },
    ]
  },

  {
    id: "certification",
    title: "Certification & Sign-Off",
    icon: "✅",
    description: "Entity certification and MLRO approval",
    questions: [
      {
        id: "certification_statement",
        question: "I certify that the information provided above is true, accurate, and complete to the best of my knowledge",
        type: "checkbox",
        required: true,
        regulatory: ["FATF"],
        label: "I certify the accuracy of this questionnaire"
      },
      {
        id: "signatory_name",
        question: "Authorized signatory name and title",
        type: "text",
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "signatory_email",
        question: "Signatory email address",
        type: "email",
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "signatory_date",
        question: "Date of certification",
        type: "date",
        required: true,
        regulatory: ["FATF"],
      },
      {
        id: "mlro_notes",
        question: "[For MLRO/Compliance Team] Internal notes and risk assessment",
        type: "textarea",
        required: false,
        internal: true,
      },
    ]
  },
];

// Helper to get questions for a section
export function getQuestionsForSection(sectionId) {
  const section = DD_QUESTIONNAIRE_SECTIONS.find(s => s.id === sectionId);
  return section?.questions || [];
}

// Helper to flatten all questions
export function getAllQuestions() {
  return DD_QUESTIONNAIRE_SECTIONS.flatMap(s => s.questions);
}

// Helper to get regulatory frameworks mentioned
export function getRegulatoryFrameworks() {
  const frameworks = new Set();
  DD_QUESTIONNAIRE_SECTIONS.forEach(section => {
    section.questions.forEach(q => {
      q.regulatory?.forEach(fw => frameworks.add(fw));
    });
  });
  return Array.from(frameworks).sort();
}
